'use client'

// Top-centre overlay that surfaces active omens. Omens are punctuated
// world events (placed by the admin panel or, eventually, by community
// governance). Each omen has { id, kind, text, intensity, until }.
// The bird sees them in its observation packet; this HUD makes them
// visible to the watcher too.

import useGameStore from '../../stores/gameStore'

export function OmenHUD() {
  // The env broadcast allocates a fresh omens array 5 times a second,
  // even empty, so selecting it re-rendered this to return null all day.
  // The joined key only changes when an omen actually appears or leaves.
  const omenKey = useGameStore((s) => (s.environment?.omens || []).map((o) => o.id || o.kind || '').join('|'))
  const omens = useGameStore.getState().environment?.omens || []
  if (!omenKey || omens.length === 0) return null

  return (
    <>
      <style jsx global>{`
        @keyframes omen-pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.72; }
        }
      `}</style>
      <div
        className="fixed top-[128px] md:top-24 left-1/2 -translate-x-1/2 z-40 pointer-events-none flex flex-col items-center gap-2"
        style={{ width: 'max-content', maxWidth: '80vw' }}
      >
        {omens.map((o) => (
          <OmenCard key={o.id} omen={o} />
        ))}
      </div>
    </>
  )
}

function OmenCard({ omen }) {
  // Tone biases the hue: positive → green, negative → red, neutral → violet.
  // Intensity controls saturation/glow strength.
  const intensity = Math.max(0, Math.min(1, omen.intensity ?? 0.6))
  const tone = omen.tone || 'neutral'
  const baseHue = tone === 'positive' ? 140 : tone === 'negative' ? 0 : 270
  const colour = `hsl(${baseHue}, 80%, 65%)`

  return (
    <div
      className="px-4 py-2 rounded-sm border"
      style={{
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(8px)',
        borderColor: colour + '80',
        boxShadow: `0 0 ${(12 + intensity * 18).toFixed(0)}px ${colour}40, inset 0 0 0 1px ${colour}20`,
        animation: 'omen-pulse 2.4s ease-in-out infinite',
      }}
    >
      {/* One row. The dot used to own a line of its own above the words,
          which read as a bullet with nothing after it. */}
      <div className="flex items-start gap-2">
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          // Nudged to the optical centre of the first line rather than the
          // top of the box, so it sits with the text at any wrap length.
          style={{ backgroundColor: colour, boxShadow: `0 0 8px ${colour}`, marginTop: 6 }}
        />
        <p className="text-[12px] font-mono italic text-white/90 leading-relaxed">
          "{omen.text}"
        </p>
      </div>
    </div>
  )
}
